// Phase 34b — Distributions section embedded on the Partnership Detail
// page. Owner is the page; this file only owns the Distributions block
// (compute, history, config). The sponsor sees the Compute / Edit-config
// controls and a preview modal. Members see history-only.
//
// Wrapped at the call site in <TabErrorBoundary tabName="Distributions">.

import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Skeleton from "@/components/ui/Skeleton";
import { toast } from "sonner";
import {
  Calculator, Download, FileSpreadsheet, FileText,
  Loader2, Settings2, TrendingUp,
} from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { useFmt } from "@/lib/useFmt";
import TierSegmentBar, { TIER_TINT } from "@/components/syndication/TierSegmentBar";

function BreakdownTable({ breakdown, fmt }) {
  // Phase 34c.3 — tier-segment bar above + tinted columns below for a
  // glance-readable visual of how the gross splits across the waterfall.
  const segmentTiers = {
    pref: breakdown.totals.pref_paid,
    return_of_capital: breakdown.totals.capital_returned,
    catch_up: breakdown.totals.gp_catch_up,
    promote: (breakdown.totals.promote_lp || 0) + (breakdown.totals.promote_gp || 0),
  };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <TierSegmentBar
          tiers={segmentTiers}
          total={breakdown.totals.gross}
          testid="dist-preview-tier-segment-bar"
        />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
      <table data-testid="dist-preview-table" className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <th className="px-4 py-3">Member</th>
            <th className="px-4 py-3">Role</th>
            <th className={`px-4 py-3 text-right ${TIER_TINT.pref.text}`}>Pref</th>
            <th className={`px-4 py-3 text-right ${TIER_TINT.return_of_capital.text}`}>Capital</th>
            <th className={`px-4 py-3 text-right ${TIER_TINT.catch_up.text}`}>Catch-up</th>
            <th className={`px-4 py-3 text-right ${TIER_TINT.promote.text}`}>Promote</th>
            <th className="px-4 py-3 text-right">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {breakdown.members.map((m) => (
            <tr key={m.member_id}>
              <td className="px-4 py-3">
                <div className="font-medium text-slate-900">{m.display_name || m.email}</div>
                {m.display_name ? <div className="text-xs text-slate-500">{m.email}</div> : null}
              </td>
              <td className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">{m.role}</td>
              <td className={`px-4 py-3 text-right tabular-nums ${TIER_TINT.pref.tint}`}>{fmt.money(m.tiers.pref)}</td>
              <td className={`px-4 py-3 text-right tabular-nums ${TIER_TINT.return_of_capital.tint}`}>{fmt.money(m.tiers.return_of_capital)}</td>
              <td className={`px-4 py-3 text-right tabular-nums ${TIER_TINT.catch_up.tint}`}>{fmt.money(m.tiers.catch_up)}</td>
              <td className={`px-4 py-3 text-right tabular-nums ${TIER_TINT.promote.tint}`}>{fmt.money(m.tiers.promote)}</td>
              <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">
                {fmt.money(m.total_received)}
              </td>
            </tr>
          ))}
          <tr data-testid="dist-preview-totals" className="bg-slate-50/60 font-semibold text-slate-900">
            <td className="px-4 py-3" colSpan={2}>Totals</td>
            <td className={`px-4 py-3 text-right tabular-nums ${TIER_TINT.pref.tint}`}>{fmt.money(breakdown.totals.pref_paid)}</td>
            <td className={`px-4 py-3 text-right tabular-nums ${TIER_TINT.return_of_capital.tint}`}>{fmt.money(breakdown.totals.capital_returned)}</td>
            <td className={`px-4 py-3 text-right tabular-nums ${TIER_TINT.catch_up.tint}`}>{fmt.money(breakdown.totals.gp_catch_up)}</td>
            <td className={`px-4 py-3 text-right tabular-nums ${TIER_TINT.promote.tint}`}>
              {fmt.money(breakdown.totals.promote_lp + breakdown.totals.promote_gp)}
            </td>
            <td className="px-4 py-3 text-right tabular-nums">{fmt.money(breakdown.totals.gross)}</td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  );
}

function ConfigModal({ open, onClose, partnershipId, config, onSaved }) {
  const [pref, setPref] = useState((config?.pref_rate ?? 0.08) * 100);
  const [promote, setPromote] = useState((config?.promote_pct ?? 0.20) * 100);
  const [catchUp, setCatchUp] = useState(config?.catch_up_enabled ?? true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (config) {
      setPref(config.pref_rate * 100);
      setPromote(config.promote_pct * 100);
      setCatchUp(config.catch_up_enabled);
    }
  }, [config]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/syndication/partnerships/${partnershipId}/waterfall-config`,
        {
          pref_rate: Number(pref) / 100,
          promote_pct: Number(promote) / 100,
          catch_up_enabled: !!catchUp,
        },
      );
      toast.success("Waterfall config saved");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save config");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="dist-config-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit waterfall configuration</DialogTitle>
          <DialogDescription>
            These values are snapshotted into every distribution run, so old
            runs keep their original numbers if you change settings later.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="cfg-pref">Pref rate (% annualized)</Label>
            <Input id="cfg-pref" type="number" min="0" max="100" step="0.01"
                   data-testid="dist-pref-rate-input"
                   value={pref} onChange={(e) => setPref(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="cfg-promote">GP promote (%)</Label>
            <Input id="cfg-promote" type="number" min="0" max="100" step="0.01"
                   data-testid="dist-promote-pct-input"
                   value={promote} onChange={(e) => setPromote(e.target.value)} required />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={catchUp}
                   onChange={(e) => setCatchUp(e.target.checked)}
                   className="h-4 w-4 rounded border-slate-300" />
            Enable GP catch-up tier
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy}
              data-testid="dist-config-save-btn"
              className="bg-indigo-600 text-white hover:bg-indigo-700">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Settings2 className="mr-2 h-4 w-4" />}
              Save config
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ComputeModal({ open, onClose, partnershipId, onFinalized, onPreviewed }) {
  const [gross, setGross] = useState("");
  const [source, setSource] = useState("rent");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const handlePreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/syndication/partnerships/${partnershipId}/distributions/preview`,
        { gross_amount: Number(gross), source, notes: notes || null },
      );
      onPreviewed(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const handleFinalize = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/syndication/partnerships/${partnershipId}/distributions/finalize`,
        { gross_amount: Number(gross), source, notes: notes || null },
      );
      toast.success(`Distribution finalized — ${data.id.slice(0, 8)}`);
      onFinalized(data);
      onClose();
      setGross(""); setNotes("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Finalize failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="dist-compute-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Compute a distribution</DialogTitle>
          <DialogDescription>
            Preview shows the breakdown without persisting balances.
            Finalize commits it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="dist-gross">Gross amount (USD)</Label>
            <Input id="dist-gross" type="number" min="0.01" step="0.01"
                   data-testid="dist-gross-input"
                   value={gross} onChange={(e) => setGross(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="dist-source">Source</Label>
            <select
              id="dist-source"
              data-testid="dist-source-select"
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              value={source} onChange={(e) => setSource(e.target.value)}
            >
              <option value="rent">Rent surplus</option>
              <option value="sale">Sale proceeds</option>
              <option value="refi">Refi proceeds</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <Label htmlFor="dist-notes">Notes (optional)</Label>
            <Input id="dist-notes" value={notes}
                   onChange={(e) => setNotes(e.target.value)} />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="button" disabled={busy || !gross}
                    onClick={handlePreview}
                    data-testid="dist-preview-btn"
                    className="border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calculator className="mr-2 h-4 w-4" />}
              Preview
            </Button>
            <Button type="button" disabled={busy || !gross}
                    onClick={handleFinalize}
                    data-testid="dist-finalize-btn"
                    className="bg-indigo-600 text-white hover:bg-indigo-700">
              <TrendingUp className="mr-2 h-4 w-4" /> Finalize &amp; save
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreviewModal({ preview, onClose, onFinalize, isSponsor }) {
  const fmt = useFmt();
  const [busy, setBusy] = useState(false);
  if (!preview) return null;
  const handleFinalize = async () => {
    setBusy(true);
    try {
      await onFinalize();
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="dist-preview-modal" className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Distribution preview · {fmt.money(preview.gross_amount)}</DialogTitle>
          <DialogDescription>
            Source: {preview.source} · This is a what-if calculation — no
            balances are persisted until you click Finalize.
          </DialogDescription>
        </DialogHeader>
        <BreakdownTable breakdown={preview.breakdown} fmt={fmt} />
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
          {isSponsor ? (
            <Button type="button" disabled={busy}
                    onClick={handleFinalize}
                    data-testid="dist-preview-finalize-btn"
                    className="bg-indigo-600 text-white hover:bg-indigo-700">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <TrendingUp className="mr-2 h-4 w-4" />}
              Finalize this distribution
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DistributionsSection({ partnership, isSponsor }) {
  const fmt = useFmt();
  const pid = partnership.id;
  const [config, setConfig] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompute, setShowCompute] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [preview, setPreview] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, list] = await Promise.all([
        api.get(`/syndication/partnerships/${pid}/waterfall-config`),
        api.get(`/syndication/partnerships/${pid}/distributions`),
      ]);
      setConfig(cfg.data);
      setRuns(list.data || []);
    } catch {
      // best-effort
    } finally { setLoading(false); }
  }, [pid]);

  useEffect(() => { refresh(); }, [refresh]);

  const finalizeFromPreview = async () => {
    try {
      const { data } = await api.post(
        `/syndication/partnerships/${pid}/distributions/finalize`,
        { gross_amount: preview.gross_amount, source: preview.source, notes: preview.notes },
      );
      toast.success(`Distribution finalized — ${data.id.slice(0, 8)}`);
      setPreview(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Finalize failed");
    }
  };

  return (
    <section data-testid="dist-section" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Distributions</h2>
          <p className="text-xs text-slate-500">
            LP-friendly American waterfall · pref &rarr; capital &rarr; catch-up &rarr; promote
            {config ? (
              <>
                {" · "}{(config.pref_rate * 100).toFixed(2)}% pref ·{" "}
                {(config.promote_pct * 100).toFixed(2)}% promote
              </>
            ) : null}
          </p>
        </div>
        {isSponsor && (
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setShowCompute(true)}
                    data-testid="dist-compute-btn"
                    className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md">
              <Calculator className="mr-2 h-4 w-4" /> Compute distribution
            </Button>
            <Button onClick={() => setShowConfig(true)} variant="outline"
                    data-testid="dist-config-btn"
                    className="border-slate-200 text-slate-700 hover:bg-slate-50">
              <Settings2 className="mr-2 h-4 w-4" /> Edit waterfall config
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        // Phase 34c.3 polish — skeleton rows instead of a spinner.
        <div data-testid="dist-history-skeleton" className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="space-y-3 p-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-24 bg-slate-100" />
                <Skeleton className="h-4 flex-1 bg-slate-100" />
                <Skeleton className="h-4 w-20 bg-slate-100" />
                <Skeleton className="h-4 w-20 bg-slate-100" />
              </div>
            ))}
          </div>
        </div>
      ) : runs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <p className="text-sm text-slate-500">
            No distributions yet — {isSponsor
              ? "click \"Compute distribution\" to model your first one."
              : "the sponsor can compute one when ready."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table data-testid="dist-history-table" className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3 text-right">Gross</th>
                <th className="px-4 py-3 text-right">Pref paid</th>
                <th className="px-4 py-3 text-right">Capital ret.</th>
                <th className="px-4 py-3">Exports</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {runs.map((r) => {
                const date = (r.finalized_at || r.computed_at || "").toString().slice(0, 10);
                return (
                  <tr key={r.id} data-testid={`dist-history-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{date}</td>
                    <td className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">{r.source}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt.money(r.gross_amount)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt.money(r.breakdown?.totals?.pref_paid || 0)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt.money(r.breakdown?.totals?.capital_returned || 0)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <a
                          href={`${API_BASE}/syndication/distributions/${r.id}/export.xlsx`}
                          data-testid={`dist-history-download-xlsx-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-indigo-600"
                        >
                          <FileSpreadsheet className="h-3.5 w-3.5" /> xlsx
                        </a>
                        <a
                          href={`${API_BASE}/syndication/distributions/${r.id}/export.pdf`}
                          data-testid={`dist-history-download-pdf-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-indigo-600"
                        >
                          <FileText className="h-3.5 w-3.5" /> pdf
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ComputeModal
        open={showCompute}
        onClose={() => setShowCompute(false)}
        partnershipId={pid}
        onPreviewed={(p) => setPreview(p)}
        onFinalized={() => refresh()}
      />
      <ConfigModal
        open={showConfig}
        onClose={() => setShowConfig(false)}
        partnershipId={pid}
        config={config}
        onSaved={(c) => setConfig(c)}
      />
      <PreviewModal
        preview={preview}
        onClose={() => setPreview(null)}
        onFinalize={finalizeFromPreview}
        isSponsor={isSponsor}
      />
    </section>
  );
}
